import { useCallback, useEffect, useRef, useState } from "react";

export function useLocalStorageState<T>(
  key: string,
  defaultValue: T,
  options?: {
    serialize?: (v: T) => string;
    deserialize?: (raw: string) => T;
  },
) {
  const serializeRef = useRef<(v: T) => string>((v) => JSON.stringify(v));
  const deserializeRef = useRef<(raw: string) => T>(
    (raw) => JSON.parse(raw) as T,
  );

  // keep latest functions without changing effect deps
  useEffect(() => {
    serializeRef.current = options?.serialize ?? ((v: T) => JSON.stringify(v));
    deserializeRef.current =
      options?.deserialize ?? ((raw: string) => JSON.parse(raw) as T);
  }, [options?.serialize, options?.deserialize]);

  const read = useCallback((): T => {
    try {
      const raw = localStorage.getItem(key);
      if (raw == null) return defaultValue;
      return deserializeRef.current(raw);
    } catch {
      return defaultValue;
    }
  }, [key, defaultValue]);

  const [value, setValue] = useState<T>(() => read());

  // re-read when key changes
  useEffect(() => {
    setValue(read());
  }, [read]);

  // save on change
  useEffect(() => {
    try {
      localStorage.setItem(key, serializeRef.current(value));
    } catch {
      // ignore
    }
  }, [key, value]);

  // sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== localStorage) return;
      if (e.key !== key) return;
      setValue(read());
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [key, read]);

  const remove = useCallback(() => {
    try {
      localStorage.removeItem(key);
    } finally {
      setValue(defaultValue);
    }
  }, [key, defaultValue]);

  return [value, setValue, remove] as const;
}
